import type { ComponentType, CSSProperties, FC, PropsWithChildren, ReactNode } from "react";

import classNames from "classnames";
import styled, { css } from "styled-components";

import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import type { IIconsButtonProps } from "@similarweb/ui-components";
import { IconButton } from "@similarweb/ui-components";

import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import { RankTrackerTooltip } from "app/components/campaigns/shared/RankTrackerTooltip";
import { CenteredFlexRow, FlexRowFlexEnd } from "pro-features/styled components/StyledFlex/src/StyledFlex";

type StyledCellButtonProps = IIconsButtonProps &
    Partial<Pick<CSSProperties, "display" | "position" | "left" | "borderColor">> & {
        isDisabled?: boolean;
        withoutText?: boolean;
        hidden?: boolean;
        padding?: string;
    };

export const StyledCellButtonText = styled(CenteredFlexRow)<{ isDisabled?: boolean }>`
    ${(props) => setFontVariant("drilldown", colorsPalettes.carbon[props.isDisabled ? 100 : 300])}
    height: 24px;
    white-space: nowrap;
`;

export const StyledIconButton = styled((props: PropsWithChildren<StyledCellButtonProps>) => <IconButton {...props} />)`
    background: ${colorsPalettes.carbon[0]};
    border-radius: 4px;
    gap: 2px;
    border: 1px solid ${({ borderColor = colorsPalettes.carbon[50] }) => borderColor};
    display: ${({ hidden, display = "flex" }) => (hidden ? "none" : display)};
    position: ${({ position = "static" }) => position};
    left: ${({ left = 0 }) => `${left}px`};
    padding: ${(props) => props.padding ?? `0px`};
    ${({ iconName }) => {
        if (!iconName) {
            return css`
                > .SWReactIcons {
                    display: none;
                }
            `;
        }
    }}

    .SWReactIcons {
        margin-right: 0;
        width: 100%;
    }

    > .SWReactIcons > svg > path {
        fill: ${(props) => colorsPalettes.carbon[props.isDisabled ? 100 : 300]};
    }

    ${({ isDisabled }) =>
        !isDisabled &&
        css`
            &:hover {
                border: 1px solid ${colorsPalettes.midnight[100]} !important;
                color: ${colorsPalettes.blue[500]};
                &:not(.enrich--disabled) {
                    background: ${colorsPalettes.blue[200]};
                    border: 1px solid ${colorsPalettes.carbon[100]};

                    .SWReactIcons > svg > path {
                        fill: ${colorsPalettes.blue[500]};
                    }

                    ${StyledCellButtonText} {
                        color: ${colorsPalettes.blue[500]};
                    }
                }
            }
        `}
`;

export interface IconButtonCellProps extends Pick<ITableCellProps, "row"> {
    buttonText: string;
    isDisabled: boolean;
    onButtonClick: (row: FixmeType) => any;
    className?: string;
    wrappedComponentTooltipText?: string;
    WrappedComponent?: ComponentType<Partial<ITableCellProps>>;
    presentJustOnHover?: boolean;
    presentExpansionButton?: boolean;
    flexDirection?: string;
    height?: string;
    beforeButton?: ReactNode;
}
const ExpansionButtonCellWrapper = styled.div<{
    flexDirection: string;
    height: string;
}>`
    justify-content: space-between;
    align-items: center;
    height: ${({ height }) => height};
    box-sizing: border-box;
    display: flex;
    flex-direction: ${({ flexDirection }) => flexDirection};
    width: 100%;
`;

const ExpansionButtonWrapper = styled(FlexRowFlexEnd)`
    background-color: transparent;
    cursor: pointer;
    gap: 8px;
    align-items: center;
`;

export const ExpansionButtonCell: FC<IconButtonCellProps> = (props) => {
    const {
        onButtonClick,
        className = "",
        buttonText,
        isDisabled,
        row,
        WrappedComponent,
        wrappedComponentTooltipText,
        presentJustOnHover = true,
        presentExpansionButton = true,
        flexDirection = "row",
        height = "100%",
        beforeButton,
    } = props;

    const buttonClickWrapper = () => {
        onButtonClick(row);
    };

    if (!presentExpansionButton && WrappedComponent) {
        return <WrappedComponent {...props} />;
    }

    return (
        <ExpansionButtonCellWrapper
            flexDirection={flexDirection}
            height={height}
        >
            {WrappedComponent ? (
                <div className={`cell-innerText ${className}`}>{<WrappedComponent {...props} />}</div>
            ) : null}
            <ExpansionButtonWrapper>
                {beforeButton}
                <RankTrackerTooltip content={wrappedComponentTooltipText}>
                    <StyledIconButton
                        height={24}
                        onClick={isDisabled ? undefined : buttonClickWrapper}
                        className={classNames(
                            "enrich",
                            presentJustOnHover && "presentJustOnHover",
                            isDisabled && "enrich--disabled",
                        )}
                        iconName={"link-out"}
                        isDisabled={isDisabled}
                        withoutText={!buttonText}
                        padding={"5px 8px 5px 4px"}
                    >
                        <StyledCellButtonText isDisabled={isDisabled}>{buttonText}</StyledCellButtonText>
                    </StyledIconButton>
                </RankTrackerTooltip>
            </ExpansionButtonWrapper>
        </ExpansionButtonCellWrapper>
    );
};
